<script setup lang="ts">
import InfluencerMark from '~/components/influencer/mark.vue'

import { useTagStore } from '~/stores/tag'

import pictureBuildHelper from '~/helpers/images/picture_build'

import type Tag from '~/entities/tag'
import type { Influencer, StatsV3Influencer } from '~/types/influencer'

type Props = {
  influencer: StatsV3Influencer | Influencer
  isContest?: boolean
  selected?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  isContest: false,
  selected: false,
})

const { t } = useI18n()

const { GET_TAGS_FROM_IDS } = useTagStore()

const picture = computed(() =>
  pictureBuildHelper({
    target: 'profile_picture',
    size: '400_400',
    slug: props.influencer.has_profile_picture
      ? props.influencer.slug
      : undefined,
    kind: 'influencer',
  }),
)

function computeCountry() {
  return (GET_TAGS_FROM_IDS(props.influencer.tags.identity.country ?? []).map(
    (tag: Tag) => tag.name,
  ) ?? [''])[0]
}

function computeKindArray() {
  if (props.isContest)
    return t('tags.influencer_kind.springboard', 0).toString()

  return GET_TAGS_FROM_IDS([
    ...new Set(props.influencer.tags.identity.influencer_kind ?? []),
  ])
    .map((tag: Tag) => t(`tags.influencer_kind.${tag.name}`))
    .join(', ')
}

// If I had more time I would investigate the use of useState for these two
const country = computeCountry()
const kindArray = computeKindArray()
</script>

<template>
  <div
    class="tw-relative tw-flex tw-h-16 tw-w-full"
    :class="{
      'tw-mb-4 tw-grid tw-h-[133px] tw-flex-col-reverse tw-gap-3': isContest,
      selected,
    }"
  >
    <div
      class="tw-flex tw-w-[calc(100%-64px-8px)] tw-flex-col tw-overflow-hidden"
    >
      <InfluencerMark
        class="tw-mb-[2px] tw-font-bold"
        :class="{ 'tw-text-discrete2': selected }"
        :influencer="influencer"
        :selected="selected"
      />
      <span
        class="tw-ellipsis tw-text-body tw-inline-block tw-font-medium sm:tw-text-body-lg sm:tw-font-bold"
        :class="{
          'tw-text-discrete2': selected,
          'tw-mb-[6px] sm:tw-mb-0': isContest,
        }"
        :data-test-id="'influencerEntityTitle_' + influencer.id"
      >
        {{ influencer.entity }}
      </span>
      <div class="tw-mt-1 tw-flex tw-flex-nowrap tw-items-center">
        <span
          class="tw-ellipsis tw-w-[unset] tw-text-sm tw-capitalize"
          :class="{ 'tw-text-discrete2': selected }"
          >{{ kindArray }}</span
        >
        <span
          v-if="$te(`tags.country.${country}`)"
          v-once
          class="tw-ml-1 tw-inline-block tw-text-sm tw-text-discrete2"
          >·</span
        >
        <span
          v-if="$te(`tags.country.${country}`)"
          v-once
          class="tw-ellipsis tw-ml-1 tw-shrink-[0.1] tw-text-sm tw-text-discrete2"
          >{{ $t(`tags.country.${country}`) }}</span
        >
      </div>
    </div>
    <img
      class="tw-ml-2 tw-size-16 tw-shrink-0 tw-rounded-full tw-shadow"
      :alt="influencer.slug"
      :class="{
        'tw-row-start-1 tw-m-0 tw-h-[55px] tw-w-full tw-rounded-xs tw-object-cover tw-object-center':
          isContest,
        'tw-opacity-20 tw-grayscale tw-filter': selected,
      }"
      :src="picture"
      loading="lazy"
    />
  </div>
</template>
